import { useAuth } from "$/util/auth";
import { getCreationBucket, getImagePath } from "$/util/creation_util";
import { createEvent, getImageUrl, useCreation, useProfile } from "$/util/db";
import { useRouteParams } from "$/util/router";
import { useSearchCreations } from "$/util/search";
import base58 from "@blackink/core/base58";
import constants from "@blackink/core/categoryConstants";
import {
  convertToFirstNameAndInitials,
  getCreationUrl,
  titleize,
  trimEllip,
} from "@blackink/core/creation/util";
import { POPULAR_TAGS } from "@blackink/core/tagLists";
import ShareIcon from "@mui/icons-material/Share";
import {
  Box,
  Button,
  Container,
  Grid,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import Banner from "./Banner";
import BaseBreadcrumbs from "./BaseBreadcrumbs";
import CreationDetailSelector from "./CreationDetailSelector";
import CreationDetailSkeleton from "./CreationDetailSkeleton";
import CreationDetailTempBanner from "./CreationDetailTempBanner";
import CreationDetailToolbar from "./CreationDetailToolbar";
import CreationHorizontalList from "./CreationHorizontalList";
import CreationImage from "./CreationImage";
import CreationTagsList from "./CreationTagsList";
import Meta from "./Meta";
import Section from "./Section";
import SocialShareModal from "./SocialShareModal";
import VisualizationModal from "./VisualizationModal";

function CreationDetailSection(props) {
  const { shortId, redirect, variant } = props;
  const creationId = base58.decode(shortId);
  const params: { style?: string } = useRouteParams();
  const [iDx, setIDx] = useState(variant);
  const [openVisualization, setOpenVisualization] = useState(false);

  const handleThumbnailClick = (i) => {
    setIDx(i);
  };

  const auth = useAuth();
  const theme = useTheme();
  const desktopScreen = useMediaQuery(theme.breakpoints.up("md"));

  const [clipboard, setClipboard] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const { data: item } = useCreation(creationId);
  let { data: relatedCreations } = useSearchCreations({
    query: item?.prompt,
    style: item?.style?.length > 0 ? item?.style[0] : "minimalist",
    limit: 12,
  });
  let { data: sameIdeaCreations } = useSearchCreations({
    query: item?.prompt,
    joiner: "|",
    limit: 12,
  });
  let { data: topCreations } = useSearchCreations({
    limit: 12,
    orderBy: "rank",
  });

  const ownerId = item?.owner;
  const { data: profile } = useProfile(ownerId);

  useEffect(() => {
    if (creationId) {
      createEvent("view", creationId, null);
    }
  }, [creationId]);

  const [relatedLink, setRelatedLink] = useState<string[]>([]);
  const [significantLink, setSignificantLink] = useState<string[]>([]);

  useEffect(() => {
    const relatedLink: string[] = [];
    if (item !== undefined && item.prompt !== undefined) {
      item.prompt.split(" ").map((word: string) => {
        if (POPULAR_TAGS.includes(word.toLowerCase())) {
          relatedLink.push(`https://blackink.ai/tatoo-ideas/${word.toLowerCase()}`);
        }
      });
      if (relatedLink.length > 0) {
        setRelatedLink(relatedLink);
      }
    }

    if (item !== undefined) {
      if (item.style.length > 0) {
        const significantLink = [];
        item.style.map((style) => {
          significantLink.push(`https://blackink.ai/styles/${style}`);
        });
        if (significantLink.length > 0) {
          setSignificantLink(significantLink);
        }
      }
    }
  }, [item]);

  // Graceful deprecation of old URL
  if (redirect && item?.id) {
    return <Navigate to={getCreationUrl(item)} />;
  }

  if (!shortId || !item || !item.id) {
    return <CreationDetailSkeleton />;
  }

  relatedCreations = relatedCreations
    ?.flatMap((page) => page)
    .filter((c) => c.id !== item.id);
  sameIdeaCreations = sameIdeaCreations?.filter((c) => c.id !== item.id);
  topCreations = topCreations?.filter((c) => c.id !== item.id);

  const date = new Date(item.createdAt).toLocaleDateString();

  let socialImg = null;
  if (item.imagePaths && item.imagePaths.length > 0) {
    socialImg = getImageUrl(item.imagePaths[iDx], getCreationBucket(item));
  }

  const blurAtIdx = item.imagePaths.length > 2 ? 3 : 1;
  const shouldBlur = !auth.isProUser && (!auth.user || auth.user.credits <= 5);

  return (
    <>
      <Meta
        title={`${constants.CATEGORY_DESC[item.style].name} "${titleize(
          trimEllip(item.prompt, 40),
        )}" Tattoo`}
        description={`Create unique ${constants.CATEGORY_DESC[
          item.style
        ].name.toLowerCase()} tattoos in seconds`}
        keywords={`${titleize(
          trimEllip(item.prompt, 40),
        ).toLowerCase()} tattoo design, ${constants.CATEGORY_DESC[
          item.style
        ].name.toLowerCase()} tattoos, tattoo designs, tattoo idea, tattoo drawing, tattoo inspiration, tattoo stencil`}
        image={socialImg}
        disableCanonicalSearch
      />
      <Helmet>
        <script type="application/ld+json" key="json-ld-shop">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": window.location.href,
            url: window.location.href,
            name: constants.CATEGORY_DESC[item.style].name.toLowerCase(),
            description: `Explore our AI-generated ${
              params.style
            } style tattoo design featuring a ${constants.CATEGORY_DESC[
              item.style
            ].name.toLowerCase()} tattoo.`,
            inLanguage: "en",
            relatedLink: relatedLink,
            significantLink: significantLink,
            isPartOf: {
              "@type": "WebSite",
              "@id": "https://blackink.ai/#website",
            },
            datePublished: item.createdAt.split("T")[0],
            creator: {
              "@type": "Organization",
              url: "https://blackink.ai/",
              sameAs: [
                "https://www.instagram.com/blackink_ai/",
                "https://twitter.com/BlackInkAI",
                "https://www.facebook.com/BlackInkAI",
              ],
              logo: "https://blackink.ai/images/logo_invert.svg",
              name: "BlackInk.AI",
              alternateName: "BlackInk AI",
              legalName: "BlackInk AI, LLC",
              foundingDate: "2021-11-01",
              description:
                "Design custom, unique tattoos in seconds with the BlackInk AI tattoo generator. Discover millions of AI-generated tattoo designs.",
              email: "info@blackink.ai",
            },
          })}
        </script>
        {item.imagePaths.length > 0 &&
          item.imagePaths.map((imgPath, idx) => (
            <script type="application/ld+json" key={idx}>
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ImageObject",
                contentUrl: `${getImageUrl(
                  getImagePath(imgPath, false, item.style[0] === "upload"),
                  getCreationBucket(item),
                )}`,
                name: `AI-Generated ${item.prompt.toLowerCase()}`,
                thumbnailUrl: `${getImageUrl(
                  getImagePath(imgPath, true, item.style[0] === "upload"),
                  getCreationBucket(item),
                )}`,
              })}
            </script>
          ))}

        <script type="application/ld+json" key="json-ld-shop">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 0,
                item: {
                  "@type": "Thing",
                  "@id": "https://blackink.ai/",
                  name: "BlackInk",
                },
              },
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Thing",
                  "@id": "https://blackink.ai/tattoo-ideas/",
                  name: "Tattoo Ideas",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <Section>
        <Container>
          <Grid container columnSpacing={4}>
            <Grid item md={6} xs={12}>
              <BaseBreadcrumbs
                categoryName="Tattoos"
                categoryPath="/tattoo-ideas"
                title={titleize(trimEllip(item.prompt, 40))}
              />

              {!desktopScreen && (
                <CreationDetailToolbar
                  item={item}
                  variant={iDx}
                  placeholder={shouldBlur && iDx >= blurAtIdx}
                />
              )}
              <Box sx={{ my: 2 }}>
                <CreationImage
                  indexToShow={iDx}
                  creation={item}
                  responsive
                  showFavoriteOverlay
                  onVisualizeClick={() => setOpenVisualization(true)}
                />
              </Box>
              <CreationDetailSelector
                item={item}
                selected={iDx}
                onClick={handleThumbnailClick}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Typography
                variant="h4"
                variantMapping={{ h4: "h1" }}
                sx={{ textTransform: "capitalize", mt: 4 }}
              >
                {titleize(trimEllip(item.prompt, 100))} Tattoo
              </Typography>
              <Typography variant="body1" variantMapping={{ body1: "h2" }}>
                Created by{" "}
                <strong>
                  {profile ? convertToFirstNameAndInitials(profile?.name) : ""}
                </strong>{" "}
                on {date}
              </Typography>

              {desktopScreen && (
                <CreationDetailToolbar
                  item={item}
                  variant={iDx}
                  placeholder={shouldBlur && iDx >= blurAtIdx}
                />
              )}

              <Stack direction="row" spacing={2} justifyContent="space-between" mt={3}>
                <CreationTagsList creation={item} />

                <Button endIcon={<ShareIcon />} onClick={handleOpenModal}>
                  Share
                </Button>
                <SocialShareModal
                  open={openModal}
                  onClose={handleCloseModal}
                  creation={item}
                  variant={iDx}
                  mediaUrl={socialImg}
                  linkUrl={null}
                />
              </Stack>

              <Banner />

              <CreationDetailTempBanner item={item} variant={iDx} />

              <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={clipboard}
                message="Copied to clipboard!"
                autoHideDuration={5000}
                onClose={() => setClipboard(false)}
              />
            </Grid>
          </Grid>

          {relatedCreations && relatedCreations.length > 0 && (
            <>
              <Typography variant="h5" variantMapping={{ h5: "h2" }} sx={{ mt: 2 }}>
                You May Also Like
              </Typography>
              <CreationHorizontalList items={relatedCreations} />
            </>
          )}
          {sameIdeaCreations && sameIdeaCreations.length > 0 && (
            <>
              <Typography variant="h5" variantMapping={{ h5: "h2" }} sx={{ mt: 2 }}>
                Similar Ideas In Different Styles
              </Typography>
              <CreationHorizontalList items={sameIdeaCreations} />
            </>
          )}
          {topCreations && topCreations.length > 0 && (
            <>
              <Typography variant="h5" variantMapping={{ h5: "h2" }} sx={{ mt: 2 }}>
                Top Picks
              </Typography>
              <CreationHorizontalList items={topCreations} disableBlur />
            </>
          )}
        </Container>
      </Section>

      <VisualizationModal
        open={openVisualization}
        onClose={() => setOpenVisualization(false)}
        imageUrl={socialImg}
        item={item}
      />
    </>
  );
}

export default CreationDetailSection;
